import { Dialog, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  PhoneDisabled as PhoneDisabledIcon,
} from "@mui/icons-material";

const OngoingCallModal = ({ open, setOpen, call_data, device, connection }) => {
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [callConnected, setCallConnected] = useState(false);
  const [callEndedByRecipient, setCallEndedByRecipient] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [speakerEnabled, setSpeakerEnabled] = useState(true); // placeholder (if controlling output is supported)

  useEffect(() => {
    let timer;

    if (open && device) {
      setSecondsElapsed(0);
      setCallConnected(false);
      setCallEndedByRecipient(false);

      const handleConnect = () => {
        setCallConnected(true);
        timer = setInterval(() => {
          setSecondsElapsed((prev) => prev + 1);
        }, 1000);
      };

      const handleDisconnect = () => {
        clearInterval(timer);
        if (callConnected) {
          setCallEndedByRecipient(true);
          setTimeout(() => {
            setCallConnected(false);
            setOpen(false);
          }, 2000);
        } else {
          setOpen(false);
        }
      };

      connection?.on("accept", handleConnect);
      connection?.on("disconnect", handleDisconnect);
      connection?.on("cancel", handleDisconnect);
      connection?.on("reject", handleDisconnect);

      return () => {
        clearInterval(timer);
        connection?.off("accept", handleConnect);
        connection?.off("disconnect", handleDisconnect);
        connection?.off("cancel", handleDisconnect);
        connection?.off("reject", handleDisconnect);
      };
    }
  }, [open, device, connection, setOpen]);

  // DTMF (touch-tone) support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (connection && e.key.match(/^[0-9*#]$/)) {
        connection.sendDigits(e.key);
      }
    };
    if (open) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, connection]);

  const toggleMute = () => {
    if (connection) {
      connection.mute(!isMuted);
      setIsMuted((prev) => !prev);
    }
  };

  const endCall = () => {
    if (device) device.disconnectAll();
    setOpen(false);
  };

  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60).toString().padStart(2, "0");
    const secsRemaining = (secs % 60).toString().padStart(2, "0");
    return `${mins}:${secsRemaining}`;
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={"xs"}
      BackdropProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.2)" } }}
      PaperProps={{ className: "rounded-xl overflow-hidden" }}
    >
      <div className="bg-white flex flex-col items-center text-center py-6 px-4 space-y-4">
        <div className="text-[24px] font-semibold text-gray-800">
          {callEndedByRecipient
            ? "Call Ended By Recipient"
            : callConnected
            ? formatTime(secondsElapsed)
            : "Ringing..."}
        </div>

        <div className="text-[16px] text-gray-600 flex flex-col items-center">
          Calling:{" "}
          <span className="font-medium">
            {call_data?.recipient || "Unknown"}
          </span>
        </div>

        <div className="flex items-center gap-4 mt-2">
          <IconButton onClick={toggleMute}>
            {isMuted ? <MicOffIcon /> : <MicIcon />}
          </IconButton>

          {/* Placeholder speaker control — typically handled by the system, or via custom AudioContext */}
          {/* <IconButton
            onClick={() => setSpeakerEnabled((prev) => !prev)}
            disabled
          >
            {speakerEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
          </IconButton> */}

          <IconButton onClick={endCall}>
            <PhoneDisabledIcon style={{ color: "red" }} />
          </IconButton>
        </div>

        <p className="text-xs text-gray-400">
          Press keypad (0-9, *, #) to send DTMF tones
        </p>
      </div>
    </Dialog>
  );
};

export default OngoingCallModal;
